import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PublicHolidaysService } from 'src/app/services/public-holidays.service';
import { PublicHoliday } from 'src/app/shared/models/general/public-holiday';

// Prefer apps/admin/src/app/data/public-holidays.helpers.ts; this page cannot import that module.
const PUBLIC_HOLIDAY_COUNTRY_ORDER = ['USA', 'ZA'] as const;
type PublicHolidayCountry = (typeof PUBLIC_HOLIDAY_COUNTRY_ORDER)[number];

interface GroupedPublicHoliday {
  date: string;
  countries: PublicHolidayCountry[];
  name: string;
  source: 'calendar' | 'manual' | 'mixed';
  holidays: PublicHoliday[];
}

@Component({
  selector: 'app-public-holidays',
  templateUrl: './public-holidays.component.html',
  styleUrls: ['./public-holidays.component.scss'],
})
export class PublicHolidaysComponent implements OnInit {
  holidays: PublicHoliday[] = [];
  loading = false;
  pageError: HttpErrorResponse;
  showError = false;

  editMode = false;
  editedDate: string | null = null;
  draftDate = '';
  draftName = '';

  showAddForm = false;
  newDate = '';
  newName = '';
  newCountries: PublicHolidayCountry[] = [...PUBLIC_HOLIDAY_COUNTRY_ORDER];
  saving = false;
  readonly minDate = this.todayIsoDate();

  constructor(private publicHolidaysService: PublicHolidaysService) {}

  get visibleHolidays(): GroupedPublicHoliday[] {
    return this.groupByDate(this.holidays.filter((holiday) => this.toDateInputValue(holiday.holidayDate) >= this.minDate));
  }

  isManualHoliday(holiday: PublicHoliday): boolean {
    return holiday.source !== 'calendar';
  }

  isManualGroup(group: GroupedPublicHoliday): boolean {
    return group.holidays.some((holiday) => this.isManualHoliday(holiday));
  }

  countryLabel(code: string): string {
    return code === 'ZA' ? 'South Africa' : code;
  }

  countriesLabel(countries: string[]): string {
    return PUBLIC_HOLIDAY_COUNTRY_ORDER.filter((code) => countries.includes(code))
      .map((code) => this.countryLabel(code))
      .join(', ');
  }

  isNewCountrySelected(country: PublicHolidayCountry): boolean {
    return this.newCountries.includes(country);
  }

  setNewCountry(country: PublicHolidayCountry, selected: boolean): void {
    const next = new Set(this.newCountries);
    if (selected) {
      next.add(country);
    } else {
      next.delete(country);
    }
    this.newCountries = PUBLIC_HOLIDAY_COUNTRY_ORDER.filter((code) => next.has(code));
  }

  sourceLabel(group: GroupedPublicHoliday): string {
    if (group.source === 'mixed') {
      return 'Calendar, Manual';
    }

    return group.source === 'calendar' ? 'Calendar' : 'Manual';
  }

  canSaveAdd(): boolean {
    return !!this.newDate && this.newCountries.length > 0;
  }

  ngOnInit(): void {
    this.loadHolidays();
  }

  loadHolidays(): void {
    this.loading = true;
    this.publicHolidaysService.getAll().subscribe({
      next: (response) => {
        this.holidays = response;
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.pageError = error;
        this.showError = true;
      },
    });
  }

  startEdit(group: GroupedPublicHoliday): void {
    const manuals = group.holidays.filter((holiday) => this.isManualHoliday(holiday));
    if (manuals.length === 0) {
      return;
    }

    this.editMode = true;
    this.editedDate = group.date;
    this.draftDate = this.toDateInputValue(manuals[0].holidayDate);
    this.draftName = manuals[0].name || '';
  }

  cancelEdit(): void {
    this.editMode = false;
    this.editedDate = null;
    this.draftDate = '';
    this.draftName = '';
  }

  async saveEdit(group: GroupedPublicHoliday): Promise<void> {
    const manuals = group.holidays.filter((holiday) => this.isManualHoliday(holiday));
    if (manuals.length === 0 || !this.draftDate) {
      return;
    }

    this.saving = true;
    try {
      for (const holiday of manuals) {
        const updated = await firstValueFrom(
          this.publicHolidaysService.update(holiday.id, {
            holidayDate: this.draftDate,
            country: holiday.country,
            name: this.draftName?.trim() || undefined,
          })
        );
        const rowIndex = this.holidays.findIndex((row) => row.id === holiday.id);
        if (rowIndex >= 0) {
          this.holidays[rowIndex] = updated;
        }
      }
      this.cancelEdit();
    } catch (error) {
      this.pageError = error as HttpErrorResponse;
      this.showError = true;
    } finally {
      this.saving = false;
    }
  }

  openAddForm(): void {
    this.cancelEdit();
    this.showAddForm = true;
    this.newDate = '';
    this.newName = '';
    this.newCountries = [...PUBLIC_HOLIDAY_COUNTRY_ORDER];
  }

  cancelAdd(): void {
    this.showAddForm = false;
    this.newDate = '';
    this.newName = '';
    this.newCountries = [...PUBLIC_HOLIDAY_COUNTRY_ORDER];
  }

  async addHoliday(): Promise<void> {
    const countries = this.newCountries;
    if (!this.newDate || countries.length === 0) {
      return;
    }

    this.saving = true;
    const created: PublicHoliday[] = [];
    try {
      for (const country of countries) {
        created.push(
          await firstValueFrom(
            this.publicHolidaysService.add({
              holidayDate: this.newDate,
              country,
              name: this.newName?.trim() || undefined,
            })
          )
        );
      }
      this.holidays = this.sortByDate([...this.holidays, ...created]);
      this.cancelAdd();
    } catch (error) {
      if (created.length > 0) {
        this.holidays = this.sortByDate([...this.holidays, ...created]);
      }
      this.pageError = error as HttpErrorResponse;
      this.showError = true;
    } finally {
      this.saving = false;
    }
  }

  async deleteHoliday(group: GroupedPublicHoliday): Promise<void> {
    const manuals = group.holidays.filter((holiday) => this.isManualHoliday(holiday));
    if (manuals.length === 0) {
      return;
    }

    if (!confirm(`Delete ${this.countriesLabel(manuals.map((row) => row.country))} holiday on ${this.formatDisplayDate(group.date)}?`)) {
      return;
    }

    this.saving = true;
    try {
      for (const holiday of manuals) {
        await firstValueFrom(this.publicHolidaysService.delete(holiday.id));
        this.holidays = this.holidays.filter((row) => row.id !== holiday.id);
      }
      if (this.editedDate && manuals.some((row) => this.toDateInputValue(row.holidayDate) === this.editedDate)) {
        this.cancelEdit();
      }
    } catch (error) {
      this.pageError = error as HttpErrorResponse;
      this.showError = true;
    } finally {
      this.saving = false;
    }
  }

  formatDisplayDate(value: string): string {
    const iso = this.toDateInputValue(value);
    if (!iso) {
      return value || '';
    }
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  }

  private sortByDate(holidays: PublicHoliday[]): PublicHoliday[] {
    return [...holidays].sort((a, b) =>
      this.toDateInputValue(a.holidayDate).localeCompare(this.toDateInputValue(b.holidayDate))
    );
  }

  private groupByDate(holidays: PublicHoliday[]): GroupedPublicHoliday[] {
    const byDate = new Map<string, PublicHoliday[]>();
    const sorted = [...holidays].sort((left, right) =>
      this.toDateInputValue(left.holidayDate).localeCompare(this.toDateInputValue(right.holidayDate))
    );

    for (const holiday of sorted) {
      const date = this.toDateInputValue(holiday.holidayDate);
      const rows = byDate.get(date) ?? [];
      rows.push(holiday);
      byDate.set(date, rows);
    }

    return [...byDate.entries()].map(([date, rows]) => {
      const countrySet = new Set(rows.map((row) => row.country));
      const names = [...new Set(rows.map((row) => (row.name || '').trim()).filter(Boolean))];
      const hasCalendar = rows.some((row) => !this.isManualHoliday(row));
      const hasManual = rows.some((row) => this.isManualHoliday(row));

      return {
        date,
        countries: PUBLIC_HOLIDAY_COUNTRY_ORDER.filter((code) => countrySet.has(code)),
        name: names.join(' / '),
        source: hasCalendar && hasManual ? 'mixed' : hasCalendar ? 'calendar' : 'manual',
        holidays: rows,
      };
    });
  }

  private todayIsoDate(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  private toDateInputValue(value: string): string {
    if (!value) {
      return '';
    }
    return value.substring(0, 10);
  }
}
